// Phase 5.2 — searchable language picker. Shows "English · Native" labels, filters by
// capability (can be native / target) and by typed text (matches English name, native
// name or code). Used for "I speak" / "I want to learn".
import { useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { nativeOptions, targetOptions, nativeOptionsFor, targetOptionsFor, getLang } from "@/lib/languages";

export default function LanguageSelect({
  value,
  onChange,
  role = "target", // "native" | "target"
  partner = null, // the other side's code, to filter to valid English-pair combinations
  disabled = false,
  placeholder = "Select a language",
  testid,
}) {
  const [open, setOpen] = useState(false);
  const options = role === "native"
    ? (partner ? nativeOptionsFor(partner) : nativeOptions())
    : (partner ? targetOptionsFor(partner) : targetOptions());
  const current = value ? getLang(value) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={testid}
          className="nb-input w-full mt-1 flex items-center justify-between gap-2 text-left"
        >
          <span className="truncate flex items-center gap-2">
            {current ? (
              <>
                <span>{current.flag}</span>
                <span className="font-medium">{current.english_name}</span>
                {current.native_name !== current.english_name && (
                  <span className="text-gray-500" dir="auto">· {current.native_name}</span>
                )}
              </>
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </span>
          <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]" align="start">
        <Command
          filter={(val, search) => {
            const l = getLang(val);
            const hay = `${l.english_name} ${l.native_name} ${l.code}`.toLowerCase();
            return hay.includes(search.toLowerCase()) ? 1 : 0;
          }}
        >
          <div className="flex items-center border-b-2 border-ink px-3">
            <Search className="w-4 h-4 opacity-50" />
            <CommandInput placeholder="Search languages…" className="border-0 focus:ring-0" data-testid={testid ? `${testid}-search` : undefined} />
          </div>
          <CommandList>
            <CommandEmpty>No language found.</CommandEmpty>
            <CommandGroup>
              {options.map((l) => (
                <CommandItem
                  key={l.code}
                  value={l.code}
                  onSelect={(v) => { onChange(v); setOpen(false); }}
                  data-testid={testid ? `${testid}-opt-${l.code}` : undefined}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <span>{l.flag}</span>
                  <span className="font-medium">{l.english_name}</span>
                  {l.native_name !== l.english_name && (
                    <span className="text-gray-500 text-sm" dir="auto">· {l.native_name}</span>
                  )}
                  {l.direction === "rtl" && <span className="ml-auto text-[10px] font-bold text-coral">RTL</span>}
                  <Check className={`w-4 h-4 ${value === l.code ? "opacity-100" : "opacity-0"} ${l.direction === "rtl" ? "" : "ml-auto"}`} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
